﻿import View, { FitOptions as BaseFitOptions } from 'ol/View';
import Feature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import MultiPolygon from 'ol/geom/MultiPolygon';
import { fromExtent } from 'ol/geom/Polygon';
import { Extent, isEmpty, buffer, getWidth, containsExtent } from 'ol/extent';

import { GeometryExtensions } from '../geometries/GeometryExtensions';
import GeometryConverter from '../geometries/GeometryConverter';

export interface FitOptions extends BaseFitOptions {
    /** Fit only if extent is outside of view or if current zoom level is higher than zoomTolerance. 
     * Default true. */
    smartFit?: boolean | {
        /** Default is 1. */
        zoomTolerance?: number
    },
    /** Delay in ms for starting fit.
     * Default is 200. */
    delay?: number,
    /** Relative adjustment of max zoom level calculated for extent.
     * Default is 0.8 
     */
    relativeZoomAdjustment?: number,
    /** Max zoom level for point geometries.
     *  Default is 10. */
    pointZoom?: number;
    /** Max zoom level for all geometries
     * Default is 20. */
    maxZoom?: number;
}

const defaults = {
    smartFit: true,
    zoomTolerance: 1,
    relativeZoomAdjustment: 0.8,
    pointZoom: 10,
    maxZoom: 20,
    delay: 200
}

export interface ViewFitExtensions {
    view: View;
    fitExtent(extent: Extent, options?: FitOptions): void;
    fitExtents(extents: Extent[], options?: FitOptions): void;
    fitGeometry(geometry: Geometry, options?: FitOptions): void;
    fitFeatures(features: Feature[], options?: FitOptions): void
}

/**
 * Composition class for openlayers View class.
 * Add extra options for fitting features, geometries or extents in a view.
 * */
class HasViewFitExtensions implements ViewFitExtensions {

    private geoExtensions: GeometryExtensions;
    private fitDelayPromise: number | undefined;

    constructor(public readonly view: View) {
        this.geoExtensions = new GeometryExtensions();
    }

    /**
     * Fits an extent into view
     * @param extent
     * @param options
     */
    public fitExtent(extent: Extent, options?: FitOptions) {
        if (!extent || isEmpty(extent)) {
            if (options?.callback)
                options.callback(false);
        }
        else {            
            const delay = Math.max(options?.delay ?? defaults.delay, 0);
            const smartfit = options?.smartFit ?? defaults.smartFit;
            const zoomTolerance = (typeof smartfit === 'boolean' || smartfit.zoomTolerance === undefined) ?
                defaults.zoomTolerance : Math.max(smartfit.zoomTolerance, 0);

            window.clearInterval(this.fitDelayPromise);
            this.fitDelayPromise = window.setTimeout(() => {

                var isInView = false;                
                if (smartfit) {                    
                    let currentExtent = this.view.calculateExtent();
                    // Decrease viewExtent by a small percentage for inView calculation, to add extra margin
                    currentExtent = buffer(currentExtent, -getWidth(currentExtent) * 0.1);
                    isInView = containsExtent(currentExtent, extent);
                }

                // Calculate zoom level required for extent
                let fitZoom = this.getFitZoomLevel(extent, options);                
                const currentZoom = this.view.getZoom();

                if (!smartfit || !isInView || currentZoom === undefined || (fitZoom - currentZoom) > zoomTolerance) {
                    this.view.fit(extent, {
                        duration: undefined,
                        padding: [0, 0, 0, 0],
                        ...(options ?? {}),
                        maxZoom: fitZoom
                    })
                }
            }, delay);
        }
    }
    public getExtent(extentsOrGeometries: Extent[] | Geometry[]): Extent | undefined {

        const extents: Extent[] = [];
        for (var value of extentsOrGeometries) {
            if (value instanceof Geometry) {
                const extent = this.groupSectionsAtDateLine(value);
                if (extent)
                    extents.push(extent);
            }
            else {
                extents.push(value);
            }
        }

        if (extents.length === 1)
            return extents[0];
        else if (extents.length) {
            const multiPoly = new MultiPolygon([]);
            extents.forEach(extent => {
                if (!isEmpty(extent))
                    multiPoly.appendPolygon(fromExtent(extent))
            })
            return this.groupSectionsAtDateLine(multiPoly);
        }
    }

    public getFitZoomLevel(extent: Extent, options?: FitOptions) {
        const maxZoom = Math.max(options?.maxZoom ?? defaults.maxZoom, 0);
        const pointZoom = Math.max(options?.pointZoom ?? defaults.pointZoom, 0);
        const relativeZoom = options?.relativeZoomAdjustment ?? defaults.relativeZoomAdjustment;

        const extentRes = this.view.getResolutionForExtent(extent);
        const extentZoom = this.view.getZoomForResolution(extentRes);

        let fitZoom: number;
        if (!extentZoom)
            fitZoom = maxZoom;
        else if (extentZoom === Infinity)
            fitZoom = pointZoom;
        else
            fitZoom = extentZoom * relativeZoom;

        return Math.ceil(Math.min(fitZoom, maxZoom));
    }

    /**
     * Fits multiple extents into view
     * Automatically groups extents at dateline
     * @param extents
     * @param options
     */
    public fitExtents(extents: Extent[], options?: FitOptions) {
        const extent = this.getExtent(extents);
        if (extent)
            this.fitExtent(extent, options);       
    }

    /**
     * Fits geometries into view
     * Automatically groups multi-point, multi-line and multi-polygons geometries at dateline
     * @param geometry
     * @param options
     */
    public fitGeometry(geometry: Geometry | Geometry[], options?: FitOptions) {
        const geometries = geometry instanceof Array ? geometry : [geometry];
        const extent = this.getExtent(geometries);
        if (extent)
            this.fitExtent(extent, options);
    }   

    /**
    * Fits geojson geometry into view
    * Automatically groups multi-point, multi-line and multi-polygons geometries at dateline
    * @param geometry
    * @param options
    */
    public fitGeoJson(geojson: GeoJSON.GeoJSON | GeoJSON.GeoJSON[], options?: FitOptions) {
        if (geojson instanceof Array) {
            const geometries: Geometry[] = [];
            geojson.forEach(g => {
                const geometry = GeometryConverter.getOpenlayersGeometry(g);
                if (geometry)
                    geometries.push(geometry);
            })

            if (geometries.length)
                this.fitGeometry(geometries, options);
        }
        else {
            const geometry = GeometryConverter.getOpenlayersGeometry(geojson);
            if (geometry)
                this.fitGeometry(geometry, options);
        }
    }  

    /**
     * Fits multiple features into view
     * Automatically groups features and multi-point, multi-line and multi-polygons geometries at dateline
     * @param features
     * @param options
     */
    public fitFeatures(features: Feature[], options?: FitOptions) {
        const geometries: Geometry[] = [];
        features.forEach(feature => {
            const geometry = feature.getGeometry();
            if (geometry)
                geometries.push(geometry);
        })

        this.fitGeometry(geometries, options);             
    }

    private groupSectionsAtDateLine(geometry: Geometry) {
        this.geoExtensions.setGeometry(geometry);
        this.geoExtensions.groupSectionsAtDateLine();
        const extent = this.geoExtensions.getGeometry()?.getExtent();
        if (extent && !isEmpty(extent))
            return extent;
        return undefined;
    }
}


export default HasViewFitExtensions;